import { createRouter, createWebHistory } from 'vue-router'
import Patterns from '../views/Patterns.vue'
import { useAuth } from '@/composables/useAuth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      // Signed-in landing leads with active projects (resume-first) rather than the
      // pattern library — users return to continue a project far more often than they
      // start a new one (#110). Finding a new pattern stays reachable via the nav.
      path: '/',
      redirect: '/projects',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
    },
    {
      path: '/patterns',
      name: 'patterns',
      component: Patterns,
      meta: { requiresAuth: true },
    },
    {
      path: '/patterns/browse',
      name: 'browse-patterns',
      component: () => import('../views/BrowsePatterns.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/patterns/import',
      name: 'import-pattern',
      component: () => import('../views/ImportPattern.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/patterns/new',
      name: 'pattern-new',
      component: () => import('../views/PatternNew.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/patterns/:id/edit',
      name: 'pattern-edit',
      component: () => import('../views/PatternEdit.vue'),
      // `tool: true` → immersive ToolLayout chrome, no website header (App.vue).
      meta: { requiresAuth: true, tool: true },
    },
    {
      path: '/patterns/:id',
      name: 'pattern-detail',
      component: () => import('../views/PatternDetail.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import('../views/Projects.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/projects/:id',
      name: 'workspace',
      component: () => import('../views/Workspace.vue'),
      // `tool: true` → immersive ToolLayout chrome, no website header (App.vue).
      meta: { requiresAuth: true, tool: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/Profile.vue'),
      meta: { requiresAuth: true },
    },
    // Legal pages are public — must be viewable before sign-in (passkey consent).
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/PrivacyPolicy.vue'),
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/TermsOfService.vue'),
    },
    // The design styleguide is public docs (#224) and renders as its own branded,
    // scrollable page — `bare: true` drops the website header/footer (App.vue) but,
    // unlike `tool`, keeps the page scrolling normally.
    {
      path: '/docs/styleguide',
      name: 'styleguide',
      component: () => import('../views/StyleGuide.vue'),
      meta: { bare: true },
    },
  ],
})

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true

  const { isAuthenticated, loading, checkSession } = useAuth()

  // On first navigation the session cookie hasn't been checked yet
  if (loading.value) {
    await checkSession()
  }

  if (!isAuthenticated.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  return true
})

export default router
